import _ from "lodash";
import ACLStore from "lib/aclStore";

import { getMemoizedLangFunction } from "Utils/GetMemoizedLangFunction";
import {
  ENABLE_ASSESSMENTS_IN_PYP,
  ENABLE_AI_TUTOR,
} from "Constants/permissionConstants";
import { ASSESSMENT_TYPE_WORKSHEET } from "Worksheet/modules/WorksheetConstants";
import {
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
  AI_TUTOR,
  WORKSHEET,
  CREATE_AI_TUTOR,
  ASSESSMENT_TYPE_AI_TUTOR,
  TASK_TYPE_QUICK_TASK,
} from "Constants/stringConstants";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import { getLearningCourseDetailsFromCache } from "Teacher/routes/LearningCourse/modules/LearningCourseGraphqlHelpers";
import { getSubjectsWithNoGradebook } from "GradingSetup/modules/Utils";
import {
  getLearningCourseVariables,
  getSelectedLearningCourseId,
} from "Teacher/routes/LearningCourse/modules/LearningCourseUtils";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";
import { getIsAssessmentTypeConfigurationEnabled } from "Utils/ACLStoreUtils";
import { getAssessmentTemplatesFromCache } from "routes/components/ChildrenPage/modules/ChildrenPageGraphqlHelpers";

// Resolves the avatar name (initials source) for a task-type icon.
// - Legacy: AI tutor / worksheet types always show "A I" / "W S".
// - AssessmentTypeConfiguration: once admin relabels the type
//   (e.g. worksheet → "Quiz"), the configured label drives the initials.
// Accepts both task-type enums ("AI_TUTOR"/"WORKSHEET") and assessment-type
// enums ("ai_tutor"/"worksheet") for `type` so it can be reused across
// dropdowns and the AssessmentIcon component.
export const getTaskTypeAvatarName = ({
  type,
  label,
  isAssessmentTypeConfigurationEnabled,
}) => {
  const isAITutorType = type === AI_TUTOR || type === ASSESSMENT_TYPE_AI_TUTOR;
  const isWorksheetType =
    type === WORKSHEET || type === ASSESSMENT_TYPE_WORKSHEET;

  if (!isAITutorType && !isWorksheetType) {
    return label;
  }

  const lowerLabel = _.toLower(label);
  const isDefaultLabel = isAITutorType
    ? lowerLabel === "ai tutor"
    : lowerLabel === "worksheet";

  // Flag OFF: always use legacy override.
  // Flag ON + admin relabeled: use the configured label so a relabeled
  // worksheet ("Quiz") shows "Q" instead of "WS".
  if (isAssessmentTypeConfigurationEnabled && !isDefaultLabel) {
    return label;
  }
  return isAITutorType ? "A I" : "W S";
};

export const getAssessmentConfig = ({ key, templateId = null, taskType }) => {
  return async (dispatch, getState) => {
    let value, groupType;

    switch (key) {
      case "CREATE_LE":
      case "CREATE_LE_INLINE": {
        value = "le";
        groupType = "LEARNING_ENGAGEMENT";
        break;
      }
      case "CREATE_FA":
      case "CREATE_FA_INLINE": {
        value = "fmt";
        groupType = "ASSESSMENT";
        break;
      }
      case "CREATE_SA":
      case "CREATE_SA_INLINE": {
        value = "smt";
        groupType = "ASSESSMENT";
        break;
      }
      case "CREATE_ASSESSMENT":
      case "CREATE_ASSESSMENT_INLINE": {
        const ubdAssessmentTypes = _.get(
          getState(),
          "platform.ubdAssessmentTypes",
          {}
        );
        value = _.get(_.keys(ubdAssessmentTypes), "[0]", "pt");
        groupType = "ASSESSMENT";
        break;
      }
      case "CREATE_QUICK_TASK":
      case "CREATE_QUICK_TASK_INLINE": {
        value = "qt";
        groupType = "QUICK_TASK";
        break;
      }
      case "CREATE_WORKSHEET":
      case "CREATE_WORKSHEET_INLINE": {
        value = "worksheet";
        groupType = "WORKSHEET";
        break;
      }
      case CREATE_AI_TUTOR: {
        value = ASSESSMENT_TYPE_AI_TUTOR;
        groupType = AI_TUTOR;
        break;
      }
      default: {
        // Handle dynamic assessment types from V2 options
        if (templateId) {
          // For dynamic assessment types, the key is the assessment type id
          value = key;
          groupType = taskType;
        }
        break;
      }
    }

    return { value, groupType, templateId };
  };
};

const getAssesmentTypeOptions = ({
  t,
  curriculumType,
  showAllOption = false,
  shouldIncludeQuickTasks = false,
  curriculumId = null,
}) => {
  const isAssessmentTypeConfigurationEnabled = getIsAssessmentTypeConfigurationEnabled();

  // If feature flag is enabled and curriculumIds provided, fetch from cache
  if (isAssessmentTypeConfigurationEnabled && curriculumId) {
    const curriculumData = getAssessmentTemplatesFromCache(curriculumId);
    const taskTypes = _.get(curriculumData, "taskTypes", []);

    const sortedTaskTypes = _.sortBy(taskTypes, "displaySequence");

    const filteredTaskTypes = !shouldIncludeQuickTasks
      ? _.filter(
          sortedTaskTypes,
          taskType =>
            !_.includes([TASK_TYPE_QUICK_TASK], _.get(taskType, "type", ""))
        )
      : sortedTaskTypes;

    const assessmentOptions = _.map(filteredTaskTypes, taskType => ({
      label: _.get(taskType, "label", ""),
      key: _.get(taskType, "id", ""),
      value: _.get(taskType, "id", ""),
    }));

    return [
      ...(showAllOption
        ? [
            {
              label: t("common:all"),
              key: "all",
              value: "all",
              type: "ALL",
            },
          ]
        : []),
      ...assessmentOptions,
    ];
  }

  // Fallback to existing logic when FF is off
  const isPYPAssessmentEnabled = ACLStore.can(ENABLE_ASSESSMENTS_IN_PYP);
  const canCreateAssessments = _.includes(
    [
      CURRICULUM_TYPE_DP,
      CURRICULUM_TYPE_MYP,
      ...(isPYPAssessmentEnabled ? [CURRICULUM_TYPE_PYP] : []),
    ],
    curriculumType
  );

  const canCreateWorksheets = ACLStore.can("FeatureFlag:ClassroomWorksheet");
  const canCreateAIChatTask = ACLStore.can(ENABLE_AI_TUTOR);

  return [
    ...(showAllOption
      ? [
          {
            label: t("common:all"),
            key: "all",
            value: "all",
            type: "ALL",
          },
        ]
      : []),
    ...(shouldIncludeQuickTasks
      ? [
          {
            label: t("common:quick_task"),
            key: "qt",
            value: "qt",
          },
        ]
      : []),
    {
      label: t("common:le_label"),
      key: "le",
      value: "le",
    },
    ...(canCreateAssessments
      ? [
          {
            label: t("common:formative_assessment"),
            key: "fmt",
            value: "fmt",
          },
          {
            label: t("common:summative_assessment"),
            key: "smt",
            value: "smt",
          },
        ]
      : []),
    ...(curriculumType === CURRICULUM_TYPE_UBD
      ? [
          {
            label: t("common:assessment_label_plural"),
            key: "assessment",
            value: "assessment",
          },
        ]
      : []),
    ...(canCreateWorksheets
      ? [
          {
            label: t("common:worksheet"),
            key: ASSESSMENT_TYPE_WORKSHEET,
            value: ASSESSMENT_TYPE_WORKSHEET,
          },
        ]
      : []),
    ...(canCreateAIChatTask
      ? [
          {
            label: t("common:ai_tutor"),
            key: ASSESSMENT_TYPE_AI_TUTOR,
            value: ASSESSMENT_TYPE_AI_TUTOR,
          },
        ]
      : []),
  ];
};

export const getAssessmentTypeDropdownMemoized = getMemoizedLangFunction(
  getAssesmentTypeOptions
);

export const getCourseSubjectType = ({ state }) => {
  const courseId = state.teacher.selected_class.selected_course;
  const learningCourseId = getSelectedLearningCourseId(state);
  const academicYearId = getSelectedAcademicYearId(state);
  const courseGradeFilters = {
    academicYearId,
  };
  const isLearningCourseEnabled = getIsLearningCoursesEnabled();
  const learningCourseVariables = getLearningCourseVariables({
    learningCourseId: learningCourseId,
    academicYearId: getSelectedAcademicYearId(state),
  });

  let course = {};
  if (isLearningCourseEnabled) {
    course = getLearningCourseDetailsFromCache(learningCourseVariables);
  } else {
    course = getCourseDetailsFromCache({
      id: courseId,
      courseGradeFilters,
    });
  }

  const subjectType = _.get(course, "subjects.0.type", "");

  return subjectType;
};

export const getIsAssessmentsOptionsDisabled = ({ state }) => {
  const subjectType = getCourseSubjectType({ state });

  const subjectsWithoutGradebook = getSubjectsWithNoGradebook();
  return _.includes(subjectsWithoutGradebook, subjectType);
};
